"use client";

import Button from "@/components/Button";
import ContentContainer from "@/components/ContentContainer/ContentContainer";
import Navigation from "@/components/Navigation";
import PricingPeriodToggle, {
  type PricingPeriodToggleValue,
} from "@/components/PricingPeriodToggle";
import { MODAL_CONTENT } from "@/constants/modalContent";
import { useMonarchMetadata } from "@/context/MonarchMetadataContext";
import clientSide from "@/helpers/clientSide";
import { useEqualHeight } from "@/hooks/useEqualHeights";
import cn from "@/lib/cn";
import { resolveCtaUrl } from "@/lib/cta-resolution";
import { parseMetadata } from "@/lib/parse-metadata";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import type {
  CTA,
  InitialToggleState,
  MobileFeaturedGridVariant,
  MobilePriceProductCardsDesignVariant,
  PriceProductCardProps,
  PriceProductCardsDesignVariant,
} from "./types";
import dynamic from "next/dynamic";
import React, { useEffect, useState, useSyncExternalStore } from "react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import PriceProductCard from "./PriceProductCard";

const Modal = dynamic(() => import("@/components/Modal"), { ssr: false });
const emptySubscribe = () => () => {};
const getServerMobileViewportSnapshot = () => false;

interface PriceProductCardsProps {
  title?: string;
  subtitle?: string;
  legalDisclaimer?: string;
  hasPeriodPricingToggle?: boolean;
  hasPriceSection?: boolean;
  hasDesktopCarousel?: boolean;
  hasDesktopOverflowPeek?: boolean;
  hasProductSelection?: boolean;
  initialToggleState?: InitialToggleState;
  mobileDesignVariant?: MobilePriceProductCardsDesignVariant;
  mobileFeaturedGridVariant?: MobileFeaturedGridVariant;
  designVariant?: PriceProductCardsDesignVariant;
  mobileInitialSlide?: number;
  customPricingLabel?: string;
  textAlign?: "left" | "center";
  products: Omit<PriceProductCardProps, "pricingView" | "sessionId">[];
  sessionId?: string;
  localizationType?: "full" | "geo" | "none";
  className?: string;
  elementName?: string;
  /** Section-level CTAs rendered below cards, above legal disclaimer */
  ctas?: CTA[];
  /** Sync card heights on mobile viewports */
  syncHeightsOnMobile?: boolean;
}

const PriceProductCards: React.FC<PriceProductCardsProps> = ({
  title = "Choose the high-power speed you need",
  subtitle,
  legalDisclaimer,
  hasPeriodPricingToggle = true,
  hasPriceSection = true,
  hasDesktopCarousel = true,
  hasDesktopOverflowPeek = true,
  hasProductSelection = false,
  initialToggleState = "one-year",
  mobileDesignVariant = "default",
  mobileFeaturedGridVariant = "default",
  designVariant = "default",
  mobileInitialSlide: configuredMobileInitialSlide,
  customPricingLabel,
  textAlign = "left",
  products,
  sessionId,
  localizationType = "none",
  className = "",
  elementName,
  ctas,
  syncHeightsOnMobile = true,
}) => {
  const getInitialPricingView = (
    toggleState: InitialToggleState
  ): PricingPeriodToggleValue => {
    if (toggleState === "five-year") {
      return "5year";
    }

    return "1year";
  };
  const isFeaturedGridMobileDesign = mobileDesignVariant === "featured-grid";
  const isSpeedCardsDesign = designVariant === "speed-cards";
  const isMobileViewport = useSyncExternalStore(
    emptySubscribe,
    () => clientSide && window.innerWidth < 768,
    getServerMobileViewportSnapshot
  );
  const initialMobileSlide =
    !isFeaturedGridMobileDesign &&
    isMobileViewport &&
    typeof configuredMobileInitialSlide === "number" &&
    configuredMobileInitialSlide >= 0
      ? configuredMobileInitialSlide
      : 0;
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [pricingView, setPricingView] = useState<PricingPeriodToggleValue>(() =>
    getInitialPricingView(initialToggleState)
  );
  const [dismissedDesktopOverflowPeekKey, setDismissedDesktopOverflowPeekKey] =
    useState<string | null>(null);
  // Modal state for both section-level and card-level CTAs (unified)
  const [activeModalKey, setActiveModalKey] = useState<string | null>(null);
  const monarchMetadata = useMonarchMetadata();

  const desktopSlidesPerView = Math.min(products.length, 4);
  const tabletSlidesPerView = Math.min(products.length, 3);
  const slidesPerView = (swiper?.params?.slidesPerView as number) || 1;
  const shouldUseStaticLayoutFromTablet = !hasDesktopCarousel;
  const shouldCenterDesktopCarouselOverflow =
    hasDesktopCarousel && products.length > desktopSlidesPerView;
  const desktopOverflowPeekKey = `${hasDesktopCarousel}-${products.length}`;
  const isDesktopOverflowPeekActive =
    hasDesktopOverflowPeek &&
    shouldCenterDesktopCarouselOverflow &&
    activeIndex === 0 &&
    dismissedDesktopOverflowPeekKey !== desktopOverflowPeekKey;
  const shouldShowNavigation = products.length > slidesPerView;
  const hasAnyTopBanner = products.some((product) =>
    Boolean(product.topBanner)
  );
  const featuredMobileCardIndex = products.findIndex((product) =>
    Boolean(product.mobileTopCard)
  );
  const featuredMobileTagLabel = products.find((product) =>
    product.mobileMostPopularTag?.trim()
  )?.mobileMostPopularTag;
  const mobileGridProducts = products.reduce<
    {
      product: Omit<PriceProductCardProps, "pricingView" | "sessionId">;
      originalIndex: number;
      isFeatured: boolean;
    }[]
  >((acc, product, index) => {
    const item = {
      product,
      originalIndex: index,
      isFeatured: index === featuredMobileCardIndex,
    };

    if (item.isFeatured) {
      acc.unshift(item);
    } else {
      acc.push(item);
    }

    return acc;
  }, []);

  // Height synchronization using useEqualHeights hook
  const heightManager = useEqualHeight(products.length, {
    syncOnMobile: syncHeightsOnMobile,
  });

  // Reset heights only when products.length changes (not on every render)
  // With ResizeObserver, heights will automatically recalculate when content changes
  useEffect(() => {
    if (heightManager) {
      heightManager.resetHeights();
    }
  }, [products.length, heightManager]);

  // Handler for card-level CTA modal clicks (passed to PriceProductCard)
  const handleCardCtaModalClick = (modalKey: string): boolean => {
    if (MODAL_CONTENT[modalKey]) {
      setActiveModalKey(modalKey);
      return true;
    }

    return false;
  };

  const sectionNamePrefix = elementName ? `${elementName}` : "";
  const isFullyLocalized = localizationType === "full";

  // Handler for section CTA clicks with action-based logic
  const handleSectionCtaClick = (cta: CTA) => {
    const isLinkAction = cta.action === "link";
    const resolvedUrl = resolveCtaUrl(cta, { pricingView });
    const actionOutcome = isLinkAction
      ? isFullyLocalized
        ? "sentToOffers"
        : "sentToLocalize"
      : "modalOpened";

    fireElementClicked({
      actionOutcome,
      elementType: "ctaButton",
      text: cta.copy,
      name: elementName
        ? `ctaButtonProductCards${sectionNamePrefix}${isLinkAction ? "Link" : "Modal"}`
        : `ctaButtonSection${cta.copy.replace(/\s+/g, "")}${isLinkAction ? "Link" : "Modal"}`,
      location: "productCards",
      outboundUrl: isLinkAction ? resolvedUrl : undefined,
      ...(monarchMetadata && { monarchMetadata }),
    });

    if (isLinkAction && resolvedUrl) {
      window.location.assign(resolvedUrl);
    } else if (!isLinkAction) {
      const modalKey = cta.modalKeyId || cta.modalKey;
      if (modalKey && MODAL_CONTENT[modalKey]) {
        setActiveModalKey(modalKey);
      }
    }
  };

  const handlePrevious = () => {
    if (isDesktopOverflowPeekActive) {
      setDismissedDesktopOverflowPeekKey(desktopOverflowPeekKey);
      return;
    }

    swiper?.slidePrev();
  };

  const handleNext = () => {
    if (isDesktopOverflowPeekActive) {
      setDismissedDesktopOverflowPeekKey(desktopOverflowPeekKey);
    }

    swiper?.slideNext();
  };

  const getTabletStaticCardPlacementClass = (index: number): string => {
    const tabletRowRemainder = products.length % 3;
    const isOnlyCardInLastTabletRow =
      tabletRowRemainder === 1 && index === products.length - 1;
    const isFirstCenteredCardInLastTabletRow =
      tabletRowRemainder === 2 && index === products.length - 2;
    const isSecondCenteredCardInLastTabletRow =
      tabletRowRemainder === 2 && index === products.length - 1;

    if (isOnlyCardInLastTabletRow) {
      return "md:col-start-3";
    }

    if (isFirstCenteredCardInLastTabletRow) {
      return "md:col-start-2";
    }

    if (isSecondCenteredCardInLastTabletRow) {
      return "md:col-start-4";
    }

    return "";
  };

  const renderCarouselCard = (
    card: Omit<PriceProductCardProps, "pricingView" | "sessionId">,
    index: number,
    cardSwiper: SwiperType | null
  ) => (
    <div
      className={
        hasAnyTopBanner
          ? "flex h-full product-card-new flex-col justify-end"
          : "flex h-full product-card-new"
      }
    >
      <PriceProductCard
        {...card}
        hasProductSelection={hasProductSelection}
        hasPriceSection={hasPriceSection}
        index={index}
        heightManager={heightManager}
        pricingView={pricingView}
        customPricingLabel={customPricingLabel}
        sessionId={sessionId}
        swiper={cardSwiper}
        localizationType={localizationType}
        textAlign={textAlign}
        designVariant={designVariant}
        onCtaModalClick={handleCardCtaModalClick}
        tagularProps={{
          position: index + 1,
        }}
        sectionElementName={elementName}
        mobileFeaturedGridVariant={mobileFeaturedGridVariant}
      />
    </div>
  );

  return (
    <ContentContainer
      spacing={isSpeedCardsDesign ? "none" : "product-cards"}
      className={cn(
        isSpeedCardsDesign ? "" : "bg-white",
        isSpeedCardsDesign
          ? "px-6 py-6 md:px-8 md:py-10 xl:px-16"
          : "pt-10 md:pt-12 lg:pt-16 xl:pt-20",
        className
      )}
    >
      {/* Title and Toggle Container - Responsive Layout */}
      <div
        className={cn(
          "flex flex-col gap-4 md:gap-8",
          isSpeedCardsDesign ? "mb-10" : "mb-6 md:mb-8"
        )}
      >
        <div
          className={cn(
            "flex flex-col gap-4 md:gap-8",
            isSpeedCardsDesign ? "items-start" : "items-center",
            hasPeriodPricingToggle &&
              "xl:flex-row xl:justify-between xl:items-center"
          )}
        >
          <div className="flex flex-col gap-5 md:gap-4">
            {/* Headline */}
            <h2
              className={cn(
                "title-lg-bold md:title-2xl-bold text-neutral-900",
                isSpeedCardsDesign
                  ? "text-left"
                  : hasPeriodPricingToggle
                    ? "text-center xl:text-left"
                    : "text-center"
              )}
              dangerouslySetInnerHTML={{ __html: parseMetadata(title) }}
            />

            {/* Description */}
            {subtitle ? (
              <p
                className={cn(
                  "text-body2",
                  isSpeedCardsDesign
                    ? "text-left"
                    : hasPeriodPricingToggle
                      ? "text-center xl:text-left"
                      : "text-center"
                )}
                dangerouslySetInnerHTML={{ __html: parseMetadata(subtitle) }}
              />
            ) : null}
          </div>

          {/* Pricing Toggle (only shown if hasPeriodPricingToggle is true) */}
          {hasPeriodPricingToggle ? (
            <PricingPeriodToggle
              value={pricingView}
              onChange={setPricingView}
              elementName={elementName}
            />
          ) : null}
        </div>
      </div>

      {/* Carousel Container */}
      {isFeaturedGridMobileDesign ? (
        <div
          className="grid grid-cols-2 gap-2.5 md:hidden"
          data-testid="featured-grid-mobile-layout"
        >
          {mobileGridProducts.map(({ product, originalIndex, isFeatured }) => {
            const cardKey =
              product.offerId ||
              product.elementName ||
              `${product.speed}-${product.unit}-${originalIndex}`;

            return (
              <div
                key={cardKey}
                className={isFeatured ? "col-span-2" : "col-span-1"}
              >
                <PriceProductCard
                  {...product}
                  hasProductSelection={hasProductSelection}
                  hasPriceSection={hasPriceSection}
                  mobileBadgeCopy={
                    isFeatured ? product.mobileBadgeCopy : undefined
                  }
                  mobileMostPopularTag={
                    isFeatured ? featuredMobileTagLabel : undefined
                  }
                  index={originalIndex}
                  heightManager={heightManager}
                  pricingView={pricingView}
                  customPricingLabel={customPricingLabel}
                  sessionId={sessionId}
                  swiper={null}
                  localizationType={localizationType}
                  textAlign={textAlign}
                  designVariant={designVariant}
                  onCtaModalClick={handleCardCtaModalClick}
                  tagularProps={{
                    position: originalIndex + 1,
                  }}
                  sectionElementName={elementName}
                  mobileDesignVariant="featured-grid"
                  mobileFeaturedGridVariant={mobileFeaturedGridVariant}
                />
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Keep the desktop overflow shift on the slide track only so nav stays visible. */}
      <div
        className={cn(
          "relative",
          isFeaturedGridMobileDesign
            ? shouldUseStaticLayoutFromTablet
              ? "hidden"
              : "hidden md:block"
            : shouldUseStaticLayoutFromTablet
              ? "md:hidden"
              : ""
        )}
        data-testid="price-product-cards-carousel-shell"
      >
        <div
          className={cn(
            "layout-content relative w-full xl:transition-transform xl:duration-300 xl:ease-out",
            isDesktopOverflowPeekActive &&
              "xl:transform-[translateX(-12.5%)]"
          )}
          data-testid="price-product-cards-carousel-track"
        >
          <Swiper
            modules={[]}
            spaceBetween={24}
            slidesPerView={1}
            slidesPerGroup={1}
            initialSlide={initialMobileSlide}
            centeredSlides={false}
            loop={false}
            navigation={false}
            wrapperClass={hasAnyTopBanner ? "items-end" : "items-stretch"}
            onSwiper={(swiperInstance) => {
              setSwiper(swiperInstance);
              setActiveIndex(swiperInstance.activeIndex);
            }}
            onSlideChange={(swiper) => {
              setActiveIndex(swiper.activeIndex);
              if (swiper.activeIndex > 0) {
                setDismissedDesktopOverflowPeekKey(desktopOverflowPeekKey);
              }
            }}
            style={{ width: "100%", overflow: "visible" }}
            autoHeight={false}
            breakpoints={{
              768: {
                slidesPerView: tabletSlidesPerView,
                slidesPerGroup: 1,
                spaceBetween: 16,
                autoHeight: false,
              },
              1280: {
                slidesPerView: desktopSlidesPerView,
                slidesPerGroup: 1,
                spaceBetween: 24,
                autoHeight: false,
              },
            }}
          >
            {products.map((card, index) => {
              const slideKey =
                card.offerId ||
                card.elementName ||
                `${card.speed}-${card.unit}-${index}`;

              return (
                <SwiperSlide
                  key={slideKey}
                  className={
                    hasAnyTopBanner || isSpeedCardsDesign
                      ? "flex h-auto w-full flex-col"
                      : "flex h-full w-full flex-col"
                  }
                >
                  {renderCarouselCard(card, index, swiper)}
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        {/* Navigation Arrows */}
        {shouldShowNavigation ? (
          <div className={cn(shouldUseStaticLayoutFromTablet && "md:hidden")}>
            <Navigation
              onPrevious={handlePrevious}
              onNext={handleNext}
              isPreviousDisabled={
                !isDesktopOverflowPeekActive &&
                (swiper ? activeIndex : initialMobileSlide) === 0
              }
              isNextDisabled={
                (swiper ? activeIndex : initialMobileSlide) >=
                products.length - (slidesPerView || 1)
              }
              tagularProps={{
                location: "productCards",
                ...(monarchMetadata && { monarchMetadata }),
              }}
              elementName={elementName}
            />
          </div>
        ) : null}
      </div>

      {shouldUseStaticLayoutFromTablet ? (
        <>
          <div
            className="hidden md:grid md:grid-cols-6 md:items-stretch md:justify-center md:gap-3 xl:hidden"
            data-testid="price-product-cards-tablet-static-layout"
          >
            {products.map((card, index) => {
              const cardKey =
                card.offerId ||
                card.elementName ||
                `${card.speed}-${card.unit}-${index}`;

              return (
                <div
                  key={cardKey}
                  className={cn(
                    "w-full min-w-0 md:col-span-2 md:justify-self-stretch",
                    getTabletStaticCardPlacementClass(index)
                  )}
                >
                  {renderCarouselCard(card, index, null)}
                </div>
              );
            })}
          </div>

          <div
            className="hidden xl:flex xl:flex-row xl:flex-nowrap xl:justify-center xl:items-stretch xl:gap-6"
            data-testid="price-product-cards-desktop-static-layout"
          >
            {products.map((card, index) => {
              const cardKey =
                card.offerId ||
                card.elementName ||
                `${card.speed}-${card.unit}-${index}`;

              return (
                <div
                  key={cardKey}
                  className="w-full min-w-0 xl:basis-0 xl:flex-1 xl:max-w-62.5"
                >
                  {renderCarouselCard(card, index, null)}
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {/* Pagination Dots */}
      {!isFeaturedGridMobileDesign && shouldShowNavigation && (
        <div
          className={cn(
            "pagination-container !my-8",
            shouldUseStaticLayoutFromTablet && "md:hidden"
          )}
        >
          {products &&
            swiper &&
            (() => {
              const totalSlides = products.length;
              const totalPositions = Math.max(
                1,
                totalSlides - slidesPerView + 1
              );

              if (totalPositions <= 1) {
                return null;
              }

              return Array.from({ length: totalPositions }, (_, i) => {
                const isActive = activeIndex === i;
                return (
                  <button
                    key={i}
                    className={`pagination-bullet ${
                      isActive ? "pagination-bullet-active" : ""
                    }`}
                    onClick={() => {
                      setDismissedDesktopOverflowPeekKey(
                        desktopOverflowPeekKey
                      );
                      swiper?.slideTo(i);
                      fireElementClicked({
                        elementType: "iconButton",
                        text: `Pagination Dot ${i + 1}`,
                        name: elementName
                          ? `iconButtonProductCards${elementName}PaginationDots${i + 1}`
                          : `iconButtonProductCardsPaginationDots${i + 1}`,
                        location: "productCards",
                        position: i + 1,
                        ...(monarchMetadata && { monarchMetadata }),
                      });
                    }}
                    aria-label={`Go to slide ${i + 1}`}
                    tabIndex={0}
                  />
                );
              });
            })()}
        </div>
      )}

      {/* Section CTAs */}
      {ctas && ctas.length > 0 && (
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          {ctas.map((cta, index) => (
            <Button
              key={index}
              onClick={() => handleSectionCtaClick(cta)}
              display={cta.display || "outline"}
              tone={cta.tone || "neutral"}
              size={cta.size || "md"}
            >
              {cta.copy}
            </Button>
          ))}
        </div>
      )}

      {/* Legal Disclaimer */}
      {legalDisclaimer ? (
        <div
          className={cn(
            "text-center px-4",
            isSpeedCardsDesign ? "mt-4" : "mt-8"
          )}
        >
          <p className="text-caption text-neutral-700">{legalDisclaimer}</p>
        </div>
      ) : null}

      {/* CTA Modal (unified for section-level and card-level CTAs) */}
      {activeModalKey && MODAL_CONTENT[activeModalKey] ? (
        <Modal
          isOpen={!!activeModalKey}
          onClose={() => setActiveModalKey(null)}
          title={MODAL_CONTENT[activeModalKey].title}
          tagularProps={{
            name: `modal${MODAL_CONTENT[activeModalKey].elementName}`,
          }}
        >
          {MODAL_CONTENT[activeModalKey].content}
        </Modal>
      ) : null}
    </ContentContainer>
  );
};

export default PriceProductCards;
